/*eslint max-lines-per-function: ["error", 220]*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Card, Tab } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import { TabsHistory } from '../../../app/components/TabsHistory';
import { SurveyTab } from '../../components/Surveys/SurveyTab';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { SurveyBasicData } from '../../components/Surveys/SurveyBasicData';
import { SurveyQuestions } from '../../components/Surveys/SurveyQuestions';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { GetSurveyDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import momencik from '../../../common/momencik';
import { SurveyEmailData } from '../../components/Surveys/SurveyEmailData';

export enum SurveyEditorTabs {
  BASIC_DATA = 'basicData',
  QUESTIONS = 'questions',
  EMAIL = 'email',
}

interface TabProps {
  key: SurveyEditorTabs;
  title: string;
  component: React.ReactNode;
  disabled: boolean;
  notSaved?: boolean;
}

const getTabClassName = (notSaved?: boolean) =>
  notSaved ? 'not-saved-tab' : '';

type ParamTypes = {
  surveyId: string;
};

export const SurveyEditorView: React.FC = () => {
  const { t, tk } = useTranslation('administrationLng');
  const { surveyId: surveyIdFromParams } = useParams<ParamTypes>();
  const pok = usePokCore();
  const notifications = useNotifications();
  const lang = pok.getLanguage();

  const [notSavedTab, setNotSavedTab] = useState<SurveyEditorTabs | undefined>(
    undefined,
  );
  const [surveyId, setSurveyId] = useState<string | undefined>(
    surveyIdFromParams,
  );
  const [survey, setSurvey] = useState<GetSurveyDto | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<string>(
    SurveyEditorTabs.BASIC_DATA,
  );

  const loadSurvey = useCallback(() => {
    if (!surveyId) {
      return;
    }

    pok.surveys
      .getById(surveyId)
      .then(surveyData => {
        setSurvey(surveyData);
      })
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      });
  }, [surveyId, pok.surveys, notifications]);

  useEffect(() => {
    if (surveyId) {
      loadSurvey();
    }
  }, [surveyId, loadSurvey]);

  const isLocked = useMemo(() => {
    if (!survey || !survey.active || !survey.startDate) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(survey.startDate);
    startDate.setHours(0, 0, 0, 0);
    return survey.active && startDate <= today;
  }, [survey]);

  const isDraft = useMemo(() => {
    if (!survey || !survey.startDate) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(survey.startDate);
    startDate.setHours(0, 0, 0, 0);
    return startDate > today;
  }, [survey]);

  const isActive = useMemo(() => {
    if (!survey || !survey.active || !survey.startDate) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(survey.startDate);
    startDate.setHours(0, 0, 0, 0);
    if (startDate > today) {
      return false;
    }
    if (!survey.endDate) {
      return true;
    }
    const endDate = new Date(survey.endDate);
    endDate.setHours(0, 0, 0, 0);
    return endDate >= today;
  }, [survey]);

  const isEnded = useMemo(() => {
    if (!survey || !survey.endDate) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(survey.endDate);
    endDate.setHours(0, 0, 0, 0);
    return endDate < today;
  }, [survey]);

  const tabs: TabProps[] = useMemo(() => {
    return [
      {
        key: SurveyEditorTabs.BASIC_DATA,
        title: t(tk.administration.basicData),
        component: (
          <SurveyBasicData
            survey={survey}
            surveyId={surveyId}
            setSurveyId={setSurveyId}
            setNotSavedTab={setNotSavedTab}
            setActiveTab={setActiveTab}
            refreshSurvey={async () => {
              await loadSurvey();
            }}
            isLocked={isLocked}
            isOtherTabInEditMode={
              !!notSavedTab && notSavedTab !== SurveyEditorTabs.BASIC_DATA
            }
          />
        ),
        disabled: false,
        notSaved: notSavedTab === SurveyEditorTabs.BASIC_DATA,
      },
      {
        key: SurveyEditorTabs.QUESTIONS,
        title: t(tk.survey.questions),
        component: (
          <SurveyQuestions
            survey={survey}
            surveyId={surveyId}
            setNotSavedTab={setNotSavedTab}
            setActiveTab={setActiveTab}
            refreshSurvey={loadSurvey}
            isLocked={
              isLocked ||
              (!!notSavedTab && notSavedTab !== SurveyEditorTabs.QUESTIONS)
            }
          />
        ),
        disabled: !surveyId,
        notSaved: notSavedTab === SurveyEditorTabs.QUESTIONS,
      },
      {
        key: SurveyEditorTabs.EMAIL,
        title: t(tk.survey.emailData),
        component: (
          <SurveyEmailData
            survey={survey}
            surveyId={surveyId}
            isLocked={isLocked}
            isOtherTabInEditMode={
              !!notSavedTab && notSavedTab !== SurveyEditorTabs.EMAIL
            }
            setNotSavedTab={setNotSavedTab}
          />
        ),
        disabled: !surveyId,
        notSaved: notSavedTab === SurveyEditorTabs.EMAIL,
      },
    ];
  }, [t, tk, survey, surveyId, notSavedTab, loadSurvey, isLocked]);

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">
          {survey ? (
            <>
              {t(tk.survey.surveyEdit)}
              {survey.name && (
                <>
                  {': '}
                  <strong>{lang === 'PL' ? survey.name : survey.nameEn}</strong>
                </>
              )}
              {`, ${t(tk.common.sysNumberLowerCase)} ${survey.systemNumber}${
                !survey.active ? ` (${t(tk.common.nonActiveShe)})` : ''
              }`}
              {survey.active && isDraft && (
                <Badge bg="info" className="ms-2 text-white">
                  {t(tk.survey.surveyScheduled)}
                </Badge>
              )}
              {survey.active && isActive && (
                <Badge bg="danger" className="ms-2 text-white">
                  {t(tk.survey.surveyActive)}
                </Badge>
              )}
              {survey.active && isEnded && (
                <Badge bg="secondary" className="ms-2">
                  {t(tk.survey.surveyEnded)}
                </Badge>
              )}
            </>
          ) : (
            t(tk.survey.surveyNew)
          )}
        </Card.Title>
        {isDraft && survey?.startDate && survey?.active && (
          <Alert variant="warning" className="mb-3">
            {t(tk.survey.surveyWillBeSentFromDate, {
              date: momencik(survey.startDate, 'LL'),
            })}
          </Alert>
        )}
        <TabsHistory
          activeKey={activeTab}
          setActiveTab={setActiveTab}
          id="survey-editor"
          className="my-3"
        >
          {tabs.map(({ key, title, component, notSaved, disabled }) => (
            <Tab
              key={key}
              eventKey={key}
              tabClassName={getTabClassName(notSaved)}
              title={`${title} ${notSaved ? `(${t(tk.survey.unwrittenUppercase)})` : ''}`}
              className="m-3"
              disabled={disabled}
            >
              <SurveyTab component={component} />
            </Tab>
          ))}
        </TabsHistory>
      </Card.Body>
    </Card>
  );
};
